/**
 * SEO component that queries for data with
 *  Gatsby's useStaticQuery React hook
 *
 * See: https://www.gatsbyjs.com/docs/use-static-query/
 */

import React from "react"
import PropTypes from "prop-types"
import { Helmet } from "react-helmet"
import { useStaticQuery, graphql } from "gatsby"
import { useLocation } from "@reach/router"
import defaultShareImage from "../assets/images/shipwreck-ext.jpg"

function SEO({ description, lang, meta, title, image }) {
  const { site } = useStaticQuery(
    graphql`
      query {
        site {
          siteMetadata {
            title
            description
            author
            siteUrl
          }
        }
      }
    `
  )

  const { pathname } = useLocation()
  const siteUrl = site.siteMetadata?.siteUrl || ``
  const metaDescription = description || site.siteMetadata.description
  const defaultTitle = site.siteMetadata?.title
  // Netlify 301s any URL containing uppercase to its lowercase form, so the
  // canonical has to be lowercase or it points at a URL that redirects. Every
  // file in src/pages is lowercase for this reason, which makes the
  // toLowerCase() a no-op today; it is a guard so that adding a capitalised
  // page filename cannot silently reintroduce a canonical/redirect mismatch.
  // Only the path is lowercased -- never the image URLs below, whose asset
  // filenames are case-sensitive.
  const canonical = `${siteUrl}${(pathname || `/`).toLowerCase()}`
  // Image imports resolve to a root-relative path; make it absolute for OG/Twitter.
  const metaImage = `${siteUrl}${image || defaultShareImage}`

  return (
    <Helmet
      htmlAttributes={{
        lang,
      }}
      title={title}
      titleTemplate={defaultTitle ? `%s | ${defaultTitle}` : null}
      link={[{ rel: `canonical`, href: canonical }]}
      meta={[
        {
          // Per-deploy-context indexing: production is indexable; branch deploys
          // and PR previews are noindex,nofollow (value set in netlify.toml).
          name: `robots`,
          content: process.env.GATSBY_ROBOTS || `index,follow`,
        },
        {
          name: `description`,
          content: metaDescription,
        },
        {
          property: `og:title`,
          content: title,
        },
        {
          property: `og:description`,
          content: metaDescription,
        },
        {
          property: `og:type`,
          content: `website`,
        },
        {
          property: `og:url`,
          content: canonical,
        },
        {
          property: `og:site_name`,
          content: defaultTitle || ``,
        },
        {
          property: `og:locale`,
          content: `en_CA`,
        },
        {
          property: `og:image`,
          content: metaImage,
        },
        {
          name: `twitter:card`,
          content: `summary_large_image`,
        },
        {
          name: `twitter:creator`,
          content: site.siteMetadata?.author || ``,
        },
        {
          name: `twitter:title`,
          content: title,
        },
        {
          name: `twitter:description`,
          content: metaDescription,
        },
        {
          name: `twitter:image`,
          content: metaImage,
        },
      ].concat(meta)}
    />
  )
}

SEO.defaultProps = {
  lang: `en`,
  meta: [],
  description: ``,
  image: ``,
}

SEO.propTypes = {
  description: PropTypes.string,
  lang: PropTypes.string,
  meta: PropTypes.arrayOf(PropTypes.object),
  title: PropTypes.string.isRequired,
  image: PropTypes.string,
}

export default SEO
